import React, { useState, useEffect, useRef, useCallback } from "react";
import { useOutletContext, useNavigate, useLocation } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";
import { theme } from "@/lib/theme";
import { Button } from "@/components/ui/button";
import { Search as SearchIcon, X } from "lucide-react";

import StyleCarousel from "@/components/chitra/StyleCarousel";
import ImagesGrid from "@/components/chitra/ImagesGrid";
import ChitraInputArea from "@/components/chitra/ChitraInputArea";
import { VGenSelectionModal } from "@/components/agents/VGenSelectionModal";
import { createVideoGeneration, startVideoStatusPolling } from "@/services/videoGenerationService";
import { ChitraImageGridSkeleton } from "@/components/ChitraImageGridSkeleton";
import dragDropImage from "@/assets/oreog-drag-and-drop.png";
import { ChitraNavigation } from "@/components/chitra/ChitraNavigation";
import { sendLocalNotification } from "@/services/pushNotifications";
import noSearchFound from "@/assets/no_search_found.png";
import { storage as storageManager } from "@/utils/storageManager";
import { consumeDailyQuota } from "@/services/dailyQuotaService";
// Theme imports
import themeRealistic from "@/assets/theme-realistic.jpg";
import themeAnime from "@/assets/theme-anime.jpg";
import theme3D from "@/assets/theme-3d.jpg";
import themeOrigami from "@/assets/theme-origami.jpg";
import themeVintage from "@/assets/theme-vintage.jpg";
import themeNeon from "@/assets/theme-neon.jpg";
import themeWatercolor from "@/assets/theme-watercolor.jpg";
import themeNone from "@/assets/theme-none.jpg";
import dfaultTheme from "@/assets/image.png"


type GeneratedImage = {
    id?: string;
    url: string;
    uploaded: boolean;
    uploading: boolean;
    prompt?: string;
    style?: string;
    cloudinaryUrl?: string;
    publicId?: string;
    createdAt?: string | Date;
    aspect_ratio?: string;
    cloudinary_public_id?: string;
    image_url?: string;
    likes_count?: number;
    user_id?: string;
    creator_name?: string;
    creator_avatar?: string;
    attached_media?: string[] | null;
    video_id?: string;
    video_url?: string;
    thumbnail_url?: string;
    status?: 'pending' | 'processing' | 'completed' | 'failed';
    isVideo?: boolean;
    isGeneratingPlaceholder?: boolean;
    processingToolName?: string;
};

type ContextType = {
    isDarkMode: boolean;
    setShowAuthModal: (show: boolean) => void;
    setShowRateLimitModal: (show: boolean) => void;
    setShowUpgradeModal?: (show: boolean) => void;
    setUpgradeResetDate?: (resetDate?: string) => void;
    user: any;
    openImageFullscreen: (img: any, onUseImage?: (dataUrl: string) => void) => void;
    openVideoFullscreen: (video: any) => void;
};

export default function ChitraHome() {
    const {
        isDarkMode,
        setShowAuthModal,
        setShowRateLimitModal,
        setShowUpgradeModal,
        setUpgradeResetDate,
        user,
        openImageFullscreen,
        openVideoFullscreen
    } = useOutletContext<ContextType>();
    const { toast } = useToast();
    const navigate = useNavigate();
    const location = useLocation();

    // State
    const [prompt, setPrompt] = useState("");
    const [isGenerating, setIsGenerating] = useState(false);
    const [generatedImages, setGeneratedImages] = useState<GeneratedImage[]>([]);
    const [visibleImages, setVisibleImages] = useState(12);
    // Fullscreen modals managed in ChitraLayout via Outlet context
    const [selectedStyle, setSelectedStyle] = useState("Auto");
    const [selectedAspectRatio, setSelectedAspectRatio] = useState("3:2");
    const [showAspectRatios, setShowAspectRatios] = useState(false);
    const [showEnhancedModal, setShowEnhancedModal] = useState(false);
    const [modalImages, setModalImages] = useState<GeneratedImage[]>([]);
    const [modalInitialIndex, setModalInitialIndex] = useState(0);
    const [isEnhancingPrompt, setIsEnhancingPrompt] = useState(false);
    const [attachedImages, setAttachedImages] = useState<string[]>([]);
    const [isEditMode, setIsEditMode] = useState(false);
    const [isEditingImage, setIsEditingImage] = useState(false);
    const [isProcessingTool, setIsProcessingTool] = useState(false);
    const [processingToolName, setProcessingToolName] = useState('');
    const [activeGenerationAspectRatio, setActiveGenerationAspectRatio] = useState<string | null>(null);
    const [isHovered, setIsHovered] = useState<string | null>(null);
    const [isDragging, setIsDragging] = useState(false);
    const [isGalleryLoading, setIsGalleryLoading] = useState(false);
    const [isVideoMode, setIsVideoMode] = useState(false);
    const [showVideoModal, setShowVideoModal] = useState(false);
    const [selectedAvatar, setSelectedAvatar] = useState<any | null>(null);
    const [selectedVoice, setSelectedVoice] = useState<any | null>(null);
    const [videoGenerations, setVideoGenerations] = useState<any[]>([]);
    const [vgCaptionsEnabled, setVgCaptionsEnabled] = useState<boolean>(false);
    const [vgBackgroundUrl, setVgBackgroundUrl] = useState<string>('');
    const [filter, setFilter] = useState("all");
    const [sortBy, setSortBy] = useState<'recent' | 'likes'>('recent');
    const [backgroundType, setBackgroundType] = useState<'solid' | 'image'>('solid');

    const [isSearchOverlayOpen, setIsSearchOverlayOpen] = useState(false);
    const [searchOverlayQuery, setSearchOverlayQuery] = useState('');

    const searchQuery = React.useMemo(() => {
        try {
            const q = new URLSearchParams(location.search).get('q') || '';
            return q.trim();
        } catch {
            return '';
        }
    }, [location.search]);

    useEffect(() => {
        const onOpen = () => {
            setSearchOverlayQuery(searchQuery || '');
            setIsSearchOverlayOpen(true);
            try {
                window.dispatchEvent(new CustomEvent('chitra:searchOverlayState', { detail: { open: true } }));
            } catch { }
        };

        window.addEventListener('chitra:openSearch', onOpen as any);
        return () => window.removeEventListener('chitra:openSearch', onOpen as any);
    }, [searchQuery]);

    const closeSearchOverlay = useCallback(() => {
        setIsSearchOverlayOpen(false);
        setSearchOverlayQuery('');
        navigate('/chitra/home', { replace: true });
        try {
            window.dispatchEvent(new CustomEvent('chitra:searchOverlayState', { detail: { open: false } }));
        } catch { }
    }, [navigate]);

    const runOverlaySearch = useCallback(() => {
        const q = searchOverlayQuery.trim();
        if (!q) return;
        navigate(`/chitra/home?q=${encodeURIComponent(q)}`);
        setIsSearchOverlayOpen(false);
        try {
            window.dispatchEvent(new CustomEvent('chitra:searchOverlayState', { detail: { open: false } }));
        } catch { }
    }, [navigate, searchOverlayQuery]);

    /*
    const [backgroundType, setBackgroundType] = useState<'solid' | 'image'>(storageManager.getAppSettings().backgroundType);

    useEffect(() => {
        const handleAppSettingsChange = (e: CustomEvent) => {
            const nextType = (e as any).detail?.backgroundType;
            if (nextType === 'solid' || nextType === 'image') {
                setBackgroundType(nextType);
            } else {
                setBackgroundType('solid');
            }
        };
        window.addEventListener('appSettingsChanged', handleAppSettingsChange as EventListener);
        return () => window.removeEventListener('appSettingsChanged', handleAppSettingsChange as EventListener);
    }, []);
    */

    const styleOptions = [
        { id: "auto", name: "Auto", thumbnail: themeNone },
        { id: "realistic", name: "Realistic", thumbnail: themeRealistic },
        { id: "anime", name: "Anime", thumbnail: themeAnime },
        { id: "3d", name: "3D", thumbnail: theme3D },
        { id: "origami", name: "Origami", thumbnail: themeOrigami },
        { id: "vintage", name: "Vintage", thumbnail: themeVintage },
        { id: "neon", name: "Neon", thumbnail: themeNeon },
        { id: "watercolor", name: "Watercolor", thumbnail: themeWatercolor },
        // New styles
        {
            id: "sketch",
            name: "Sketch",
            thumbnail: dfaultTheme,
            prompt: "Pencil sketch style, graphite shading, cross-hatching, rough paper grain, high contrast lines, hand-drawn contours, minimal tones, illustrative look \n\n"
        },
        {
            id: "fisheye",
            name: "Fisheye",
            thumbnail: dfaultTheme,
            prompt: "Ultra-wide fisheye lens effect, strong barrel distortion, circular frame feel, dramatic perspective, center emphasis, edge stretch, photographic look \n\n"
        },
        {
            id: "popart",
            name: "Pop art",
            thumbnail: dfaultTheme,
            prompt: "Pop art style, halftone dots, bold outlines, flat vibrant colors, comic-book aesthetic, high contrast poster look, retro print textures \n\n"
        },
        {
            id: "ornament",
            name: "Ornament",
            thumbnail: dfaultTheme,
            prompt: "Ornate decorative style, intricate filigree, gilded metallic highlights, soft bokeh lights, festive ambience, elegant craftsmanship \n\n"
        },
        {
            id: "bobblehead",
            name: "Bobblehead",
            thumbnail: dfaultTheme,
            prompt: "Stylized bobblehead character, oversized head, small body proportions, glossy 3D toy material, soft studio lighting, cute collectible vibe \n\n"
        },
        {
            id: "crystals",
            name: "Crystals",
            thumbnail: dfaultTheme,
            prompt: "Crystalline aesthetic, faceted geometry, refractive surfaces, sparkles and glints, prismatic light dispersion, glassy translucent materials \n\n"
        },
        {
            id: "sticker",
            name: "Sticker",
            thumbnail: dfaultTheme,
            prompt: "Glossy sticker style, bold outline, drop shadow, high contrast, simple flat shading, die-cut edge look, playful graphic design \n\n"
        },
    ];

    // Fetch images (All/Community)
    const fetchGeneratedImages = useCallback(async () => {
        try {
            setIsGalleryLoading(true);
            const { data, error } = await supabase.functions.invoke("get-gallery-images", {
                body: {
                    offset: 0,
                    limit: 50, // Limit for home view
                    userId: null, // Public feed
                    sortBy: sortBy,
                    ownerOnly: false,
                },
            });

            if (error || !data?.success) throw error || new Error('Failed to fetch images');

            const images = (data.images || []) as any[];
            const videosResp = (data.videos || []) as any[];

            const formattedVideos = videosResp.map((v: any) => ({
                id: v.id,
                video_id: v.video_id,
                url: v.thumbnail_url || v.video_url || "",
                video_url: v.video_url,
                thumbnail_url: v.thumbnail_url,
                status: v.status,
                uploaded: v.status === 'completed',
                uploading: v.status === 'pending' || v.status === 'processing',
                prompt: v.prompt,
                createdAt: v.created_at,
                user_id: v.user_id,
                isVideo: true,
            } as GeneratedImage));

            if (images && images.length > 0) {
                const userIds = [...new Set(images.map((img: any) => img.user_id).filter(Boolean))];
                const { data: profiles } = userIds.length
                    ? await supabase.from("user_profiles").select("user_id, full_name, avatar_url").in("user_id", userIds)
                    : { data: [] } as any;

                type UserProfile = { user_id: string; full_name?: string; avatar_url?: string };
                const profileMap = new Map<string, UserProfile>((profiles || []).map((p: any) => [p.user_id, p]));

                const formattedImages: GeneratedImage[] = images.map((img: any) => {
                    const userProfile = profileMap.get(img.user_id);
                    return {
                        id: img.id,
                        url: img.image_url || "",
                        uploaded: true,
                        uploading: false,
                        prompt: img.prompt,
                        style: img.style,
                        cloudinaryUrl: img.image_url,
                        // publicId removed from DB; keep undefined locally
                        createdAt: img.created_at,
                        aspect_ratio: img.aspect_ratio,
                        likes_count: img.likes_count,
                        user_id: img.user_id,
                        attached_media: img.attached_media ?? null,
                        creator_name: userProfile?.full_name ?? "",
                        creator_avatar: userProfile?.avatar_url,
                    };
                });

                setGeneratedImages(formattedImages);
                setVideoGenerations(formattedVideos);
            }
        } catch (error) {
            console.error("Error fetching generated images:", error);
        } finally {
            setIsGalleryLoading(false);
        }
    }, [sortBy]);

    useEffect(() => {
        fetchGeneratedImages();

        // Request notification permission if not already granted/denied
        if ('Notification' in window && Notification.permission === 'default') {
            Notification.requestPermission().then(permission => {
            }).catch(err => {
                console.warn('Error requesting notification permission:', err);
            });
        }
    }, [fetchGeneratedImages]);

    // Track the aspect ratio used for the current generation so the loader tile
    // doesn't change shape if the user adjusts the selector mid-generation.
    useEffect(() => {
        const anyGenerating = isGenerating || isEditingImage || isProcessingTool;
        if (anyGenerating) {
            setActiveGenerationAspectRatio((prev) => prev || selectedAspectRatio);
        } else {
            setActiveGenerationAspectRatio(null);
        }
    }, [isGenerating, isEditingImage, isProcessingTool, selectedAspectRatio]);

    // Merge videos and images for display
    const allItems = React.useMemo(() => {
        const videos = videoGenerations.map(v => ({
            id: v.id,
            url: v.video_url || v.thumbnail_url || '',
            video_url: v.video_url,
            thumbnail_url: v.thumbnail_url,
            uploaded: v.status === 'completed',
            uploading: v.status === 'pending' || v.status === 'processing',
            prompt: v.prompt,
            style: 'Video',
            createdAt: v.created_at,
            user_id: v.user_id,
            video_id: v.video_id,
            status: v.status,
            isVideo: true,
            isVideoItem: true,
        } as GeneratedImage));

        let items: GeneratedImage[] = [] as any;
        if (filter === 'videos') {
            items = videos as any;
        } else if (filter === 'images') {
            items = [...generatedImages] as any;
        } else {
            items = [...videos as any, ...generatedImages as any];
        }

        // Client-side sorting fallback
        items.sort((a, b) => {
            if (sortBy === 'likes') {
                const aLikes = a.likes_count ?? 0;
                const bLikes = b.likes_count ?? 0;
                if (bLikes !== aLikes) return bLikes - aLikes;
            }
            const aTime = new Date(a.createdAt || 0).getTime();
            const bTime = new Date(b.createdAt || 0).getTime();
            return bTime - aTime;
        });

        if (isGenerating || isEditingImage || isProcessingTool) {
            const placeholder: GeneratedImage = {
                id: 'generating-placeholder',
                url: '',
                uploaded: false,
                uploading: true,
                isGeneratingPlaceholder: true,
                processingToolName: processingToolName,
                createdAt: new Date().toISOString(),
                aspect_ratio: activeGenerationAspectRatio || selectedAspectRatio,
            };
            items.unshift(placeholder);
        }

        return items;
    }, [videoGenerations, generatedImages, filter, sortBy, isGenerating, isEditingImage, isProcessingTool, processingToolName, selectedAspectRatio, activeGenerationAspectRatio]);

    const searchedItems = React.useMemo(() => {
        if (!searchQuery) return allItems;
        const q = searchQuery.toLowerCase();
        return (allItems || []).filter((it: any) => {
            if (it?.isGeneratingPlaceholder) return true;
            if (it?.isVideo) return false;
            const p = (it?.prompt || '').toString().toLowerCase();
            return p.includes(q);
        });
    }, [allItems, searchQuery]);

    // Generation Logic
    const handleGenerate = async () => {
        if (!prompt.trim()) return;

        try {
            let canProceed = true;
            try {
                if (user?.id) {
                    const quotaRes = await consumeDailyQuota('image', 1);
                    if (!quotaRes?.allowed) {
                        (setShowRateLimitModal as any)?.(false);
                        (setUpgradeResetDate as any)?.(quotaRes?.reset_date);
                        (setShowUpgradeModal as any)?.(true);
                        toast({ title: "Daily limit reached", description: quotaRes?.reason || "Daily image limit reached.", variant: "destructive" });
                        return;
                    }
                }

                const { data: rateLimitData } = await supabase.functions.invoke("check-rate-limit", {
                    body: { userId: user?.id || null },
                });

                if (rateLimitData && !rateLimitData.canProceed) {
                    if (!user) {
                        setShowRateLimitModal(true);
                        return;
                    } else {
                        (setUpgradeResetDate as any)?.(undefined);
                        (setShowUpgradeModal as any)?.(true);
                        toast({ title: "Rate Limit", description: "Daily limit reached.", variant: "destructive" });
                        return;
                    }
                }
            } catch (rateError) {
                console.warn("Rate limit check failed, proceeding anyway:", rateError);
                // Fail open: assume user can proceed if check fails
            }

            setIsGenerating(true);
            const sentPrompt = prompt;
            const sentStyle = selectedStyle;
            const sentAspect = selectedAspectRatio;

            // Prepare attached images: upload any data: images to Cloudinary, keep existing URLs
            const uploadedAttachedUrls = await Promise.all(
                attachedImages.map(async (img) =>
                    img.startsWith('data:') ? await uploadBase64ToCloudinary(img) : img
                )
            );
            const validAttachedUrls = uploadedAttachedUrls.filter(
                (u) => typeof u === 'string' && /^https?:\/\//.test(u)
            );

            let enhancedPrompt = prompt.trim();
            if (selectedStyle.toLowerCase() !== "auto") {
                const stylePrompts: Record<string, string> = {
                    realistic:
                        "ultra-realistic photography style, cinematic studio lighting, professional camera capture, 8K-level detail, lifelike skin and material textures, shallow depth of field, natural color grading, high dynamic range",

                    anime:
                        ", anime illustration style, expressive facial features, dynamic character posing, vibrant cel-shading, crisp clean line art, detailed eyes, Japanese manga and anime aesthetics",

                    "3d":
                        ", premium high-fidelity 3D render, octane or unreal engine quality, physically based materials, global illumination, volumetric lighting, soft realistic shadows, cinematic composition, sharp focus",

                    origami:
                        ", origami-inspired paper sculpture style, precise folded geometry, clean edges and creases, minimalist composition, matte paper textures, subtle ambient shadows, elegant structural symmetry",

                    vintage:
                        ", vintage film photography aesthetic, analog camera look, warm retro color grading, faded tones, fine film grain, slight vignette, nostalgic atmosphere, classic framing",

                    neon:
                        ", neon cyberpunk aesthetic, intense glowing light accents, high-contrast color palette, futuristic city ambience, reflective surfaces, electric atmosphere, dark moody background",

                    watercolor:
                        ", watercolor painting style, soft flowing pigments, organic color blooms, gentle gradients, textured cold-press paper, delicate brush transitions, airy and painterly feel",

                    sketch:
                        ", hand-drawn pencil sketch style, razor-sharp graphite linework, intricate cross-hatching and stippling, realistic pressure variation, visible paper fibers, monochrome tonal depth, refined illustrative quality",

                    fisheye:
                        ", fisheye lens perspective, extreme 180-degree ultra-wide angle, pronounced barrel distortion, curved edges and horizon lines, exaggerated depth, center-weighted composition, immersive photographic look",

                    popart:
                        ", 1960s comic-book pop-art illustration, thick inked black outlines, precision halftone dot patterns, flat hyper-saturated primary colors, expressive exaggerated features, offset print texture, bold graphic energy",

                    ornament:
                        ", glossy glass ornament sculpture style, flawless molded surface, deep lacquered enamel coating, high-specular reflections, realistic light refraction and bloom, metallic topper detail, luxury product render",

                    bobblehead:
                        ", bobblehead figurine style, exaggerated oversized head and compact body proportions, finely sculpted caricatured facial features, smooth painted resin texture, subtle plastic sheen, premium collectible toy photography",

                    crystals:
                        ", crystalline sculpture aesthetic, hyper-precise faceted geometry, translucent gemstone materials, physically accurate light refraction, internal caustics, prismatic spectral highlights, macro mineral realism",

                    sticker:
                        ", premium sticker illustration style, ultra-clean vector-like outlines, bold vibrant flat colors, sharp edge separation, subtle print grain, accurate die-cut contour, high-quality vinyl finish",
                };

                enhancedPrompt += stylePrompts[selectedStyle.toLowerCase()] || "";
            }

            const { data: imageData, error: imageError } = await supabase.functions.invoke("universal-image-generation", {
                body: {
                    prompt: enhancedPrompt,
                    images: validAttachedUrls,
                    aspectRatio: sentAspect,
                    style: sentStyle,
                    userId: user?.id,
                    origin: 'chitra'
                },
            });

            if (imageError || !imageData?.success || !imageData?.image) {
                throw new Error(imageError?.message || imageData?.error || "Failed to generate image");
            }

            setTimeout(() => {
                setPrompt("");
                setAttachedImages([]);
                setIsEditMode(false);
            }, 100);

            const newImage: GeneratedImage = {
                id: imageData.databaseId, // Use the DB ID returned from edge function
                url: imageData.image,
                uploaded: true, // It's already saved in DB
                uploading: false,
                prompt: sentPrompt,
                style: sentStyle,
                aspect_ratio: sentAspect,
                createdAt: new Date().toISOString(),
                user_id: user?.id,
                cloudinaryUrl: imageData.image, // For compatibility
                image_url: imageData.image,
                attached_media: validAttachedUrls.length ? validAttachedUrls : null,
            };

            setGeneratedImages((prev) => [newImage, ...prev]);

            const truncatedPrompt = sentPrompt.length > 50
                ? sentPrompt.substring(0, 50) + "..."
                : sentPrompt;

            // Send notification as requested
            sendLocalNotification("Your image is ready to see!", {
                body: truncatedPrompt,
                image: imageData.image,
                icon: "/image.png",
                data: {
                    url: imageData.databaseId ? `/chitra/library?imageId=${imageData.databaseId}` : "/chitra/library",
                },
                silent: false,
            } as any)

        } catch (error: any) {
            console.error("Error generating image:", error);
            toast({ title: "Error", description: error.message, variant: "destructive" });
        } finally {
            setIsGenerating(false);
        }
    };

    const uploadToCloudinary = async (image: GeneratedImage) => {
        try {
            const isRemoteUrl = image.url.startsWith('http');
            const payload = {
                prompt: image.prompt,
                userId: user?.id,
                style: image.style,
                aspectRatio: image.aspect_ratio || "3:2",
                // Pass imageUrl if it's a URL, otherwise imageBase64
                ...(isRemoteUrl ? { imageUrl: image.url } : { imageBase64: image.url }),
            };

            const { data: uploadResult, error } = await supabase.functions.invoke("upload-to-cloudinary", {
                body: payload,
            });

            if (error || !uploadResult?.success) throw new Error(error?.message || uploadResult?.error);

            setGeneratedImages((prev) =>
                prev.map((img) =>
                    img.id === image.id
                        ? { ...img, url: uploadResult.imageUrl, id: uploadResult.databaseId, uploaded: true, uploading: false, cloudinaryUrl: uploadResult.imageUrl }
                        : img
                )
            );
        } catch (error: any) {
            console.error("Upload failed:", error);
            setGeneratedImages((prev) => prev.map((img) => img.id === image.id ? { ...img, uploading: false, uploaded: false } : img));
        }
    };

    const uploadBase64ToCloudinary = async (base64Data: string): Promise<string> => {
        try {
            const cloudName = "dcqtcd2zs";
            const uploadPreset = "image_server_1";
            const folder = "images";

            const prepareBase64 = (src: string) => new Promise<string>((resolve, reject) => {
                if (!src.startsWith('data:')) return resolve(src);
                const img = new Image();
                img.crossOrigin = 'anonymous';
                img.onload = () => {
                    const maxW = 1280;
                    const maxH = 1280;
                    let w = img.naturalWidth || img.width;
                    let h = img.naturalHeight || img.height;
                    const scale = Math.min(1, maxW / Math.max(1, w), maxH / Math.max(1, h));
                    w = Math.max(1, Math.round(w * scale));
                    h = Math.max(1, Math.round(h * scale));
                    const canvas = document.createElement('canvas');
                    canvas.width = w;
                    canvas.height = h;
                    const ctx = canvas.getContext('2d');
                    if (!ctx) return resolve(src);
                    ctx.drawImage(img, 0, 0, w, h);
                    try {
                        const out = canvas.toDataURL('image/jpeg', 0.7);
                        resolve(out);
                    } catch {
                        resolve(src);
                    }
                };
                img.onerror = () => resolve(src);
                img.src = src;
            });

            const optimizedBase64 = await prepareBase64(base64Data);

            const formData = new FormData();
            formData.append("file", optimizedBase64);
            formData.append("upload_preset", uploadPreset);
            formData.append("folder", folder);

            const response = await fetch(`https://api.cloudinary.com/v1_1/${cloudName}/image/upload`, {
                method: "POST",
                body: formData,
            });

            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(`Cloudinary upload failed: ${response.status} ${errorText}`);
            }

            const data = await response.json();
            return data.secure_url;
        } catch (error: any) {
            console.error("Input upload error:", error);
            throw error;
        }
    };

    const handleVideoGenerate = async (params: { text: string; avatar_id?: string; voice_id?: string }) => {
        try {
            const videoId = await createVideoGeneration({
                ...params,
                user_id: user?.id,
                captions_enabled: vgCaptionsEnabled,
                background_image_url: vgBackgroundUrl || undefined,
            });

            const newVideo = {
                id: videoId,
                video_id: videoId,
                status: 'pending',
                prompt: params.text,
                created_at: new Date().toISOString(),
                user_id: user?.id,
            };

            setVideoGenerations(prev => [newVideo, ...prev]);
            startVideoStatusPolling(videoId, (status) => {
                setVideoGenerations(prev => prev.map(v => v.video_id === videoId ? { ...v, ...status } : v));

                if (status.status === 'completed') {
                    sendLocalNotification("Your video is ready to see!", {
                        body: params.text,
                        image: status.thumbnail_url || vgBackgroundUrl,
                        icon: "/favicon.ico",
                        data: {
                            url: videoId ? `/chitra/library?videoId=${videoId}` : "/chitra/library",
                        },
                        silent: false,
                    } as any);
                }
            });

            toast({ title: "Video Generation Started" });
            setIsVideoMode(false);
            setSelectedAvatar(null);
            setSelectedVoice(null);
        } catch (error: any) {
            toast({ title: "Generation Failed", description: error.message, variant: "destructive" });
        }
    };

    const handleEditImage = async () => {
        if (attachedImages.length === 0 || !prompt.trim()) {
            toast({ title: "Missing Requirements", description: "Please attach image(s) and describe what you want.", variant: "destructive" });
            return;
        }

        setIsEditingImage(true);
        const editPrompt = prompt;

        setTimeout(() => {
            setPrompt("");
            setAttachedImages([]);
            setIsEditMode(false);
        }, 100);

        try {
            let enhancedPrompt = prompt.trim();

            // Upload any locally attached (data:) images to Cloudinary at send-time
            const uploadedImageUrls = await Promise.all(
                attachedImages.map(async (img) =>
                    img.startsWith('data:') ? await uploadBase64ToCloudinary(img) : img
                )
            );

            // Ensure we only send valid HTTP(S) URLs to the edge function
            const validImageUrls = uploadedImageUrls.filter(
                (u) => typeof u === 'string' && /^https?:\/\//.test(u)
            );
            if (validImageUrls.length === 0) {
                throw new Error("No valid image URLs after upload. Please reattach your image(s) and try again.");
            }

            const { data: editData, error: editError } = await supabase.functions.invoke("imageEdit", {
                body: {
                    prompt: enhancedPrompt,
                    images: validImageUrls,
                    aspectRatio: selectedAspectRatio,
                    style: selectedStyle,
                    userId: user?.id
                },
            });

            if (editError || !editData?.success) throw new Error(editData?.error || "Failed to edit image");

            if (editData?.image) {
                const finalUrl = editData.image;

                const newImage: GeneratedImage = {
                    id: editData.databaseId,
                    url: finalUrl,
                    uploaded: true,
                    uploading: false,
                    prompt: `Edited: ${editPrompt}`,
                    style: "Image Edit",
                    aspect_ratio: selectedAspectRatio || "1:1",
                    createdAt: new Date().toISOString(),
                    user_id: user?.id,
                    cloudinaryUrl: finalUrl,
                    image_url: finalUrl
                };
                setGeneratedImages((prev) => [newImage, ...prev]);
            }
        } catch (error: any) {
            toast({ title: "Error", description: error.message, variant: "destructive" });
        } finally {
            setIsEditingImage(false);
        }
    };

    const enhancePrompt = async () => {
        if (!prompt.trim()) {
            toast({ title: "Error", description: "Please enter a prompt first", variant: "destructive" });
            return;
        }
        setIsEnhancingPrompt(true);
        try {
            // Upload any locally attached (data:) images to Cloudinary at send-time
            const uploadedImageUrls = await Promise.all(
                attachedImages.map(async (img) =>
                    img.startsWith('data:') ? await uploadBase64ToCloudinary(img) : img
                )
            );
            const validImageUrls = uploadedImageUrls.filter(
                (u) => typeof u === 'string' && /^https?:\/\//.test(u)
            );

            const { data, error } = await supabase.functions.invoke("quickTask", {
                body: { prompt: prompt.trim(), images: validImageUrls, mode: 'enhance' },
            });
            if (error || !data?.success) throw new Error(data?.error);
            if (data.enhancedPrompt) setPrompt(data.enhancedPrompt);
        } catch (error: any) {
            toast({ title: "Error", description: error.message, variant: "destructive" });
        } finally {
            setIsEnhancingPrompt(false);
        }
    };

    const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files ? Array.from(event.target.files) : [];
        const images = files.filter((f) => f.type.startsWith("image/"));
        if (images.length === 0) return;

        const remainingSlots = Math.max(0, 4 - attachedImages.length);
        const count = Math.min(remainingSlots, images.length);
        const toProcess = images.slice(0, count);

        // 1) Immediately show placeholders with loaders
        if (count > 0) {
            setAttachedImages((prev) => {
                const next = prev.slice(0, 4);
                const placeholders = Array(count).fill("LOADING");
                const merged = [...next, ...placeholders].slice(0, 4);
                setIsEditMode(merged.length > 0);
                return merged;
            });
        }

        // 2) Start uploads in background and replace placeholders as each finishes
        toProcess.forEach((file) => {
            const reader = new FileReader();
            reader.onload = async (e) => {
                try {
                    const dataUrl = e.target?.result as string;
                    const finalUrl = dataUrl.startsWith('data:')
                        ? await uploadBase64ToCloudinary(dataUrl)
                        : dataUrl;
                    setAttachedImages((prev) => {
                        const idx = prev.indexOf("LOADING");
                        if (idx !== -1) {
                            const next = [...prev];
                            next[idx] = finalUrl;
                            return next;
                        }
                        return prev.length < 4 ? [...prev, finalUrl] : prev;
                    });
                } catch (err) {
                    // Remove one placeholder on failure
                    setAttachedImages((prev) => {
                        const idx = prev.indexOf("LOADING");
                        if (idx !== -1) {
                            const next = [...prev];
                            next.splice(idx, 1);
                            return next;
                        }
                        return prev;
                    });
                    toast({ title: "Attach failed", description: "Could not upload some images.", variant: "destructive" });
                }
            };
            reader.onerror = () => {
                setAttachedImages((prev) => {
                    const idx = prev.indexOf("LOADING");
                    if (idx !== -1) {
                        const next = [...prev];
                        next.splice(idx, 1);
                        return next;
                    }
                    return prev;
                });
                toast({ title: "Attach failed", description: "Could not read image.", variant: "destructive" });
            };
            reader.readAsDataURL(file);
        });
    };

    // Drag and drop
    useEffect(() => {
        const handleDragOver = (e: DragEvent) => {
            e.preventDefault();
            e.stopPropagation();
            if (!isDragging) setIsDragging(true);
        };
        const handleDragLeave = (e: DragEvent) => {
            e.preventDefault();
            e.stopPropagation();
            if (isDragging && e.relatedTarget === null) setIsDragging(false);
        };
        const handleDrop = async (e: DragEvent) => {
            e.preventDefault();
            e.stopPropagation();
            setIsDragging(false);
            if (e.dataTransfer?.files && e.dataTransfer.files.length > 0) {
                const files = Array.from(e.dataTransfer.files).filter((f) => f.type.startsWith("image/"));
                if (files.length === 0) return;

                const remainingSlots = Math.max(0, 4 - attachedImages.length);
                const count = Math.min(remainingSlots, files.length);
                const toRead = files.slice(0, count);

                if (count > 0) {
                    setAttachedImages((prev) => {
                        const next = prev.slice(0, 4);
                        const placeholders = Array(count).fill("LOADING");
                        const merged = [...next, ...placeholders].slice(0, 4);
                        setIsEditMode(merged.length > 0);
                        return merged;
                    });
                }

                toRead.forEach((file) => {
                    const reader = new FileReader();
                    reader.onload = async (ev) => {
                        try {
                            const dataUrl = ev.target?.result as string;
                            const finalUrl = dataUrl.startsWith('data:')
                                ? await uploadBase64ToCloudinary(dataUrl)
                                : dataUrl;
                            setAttachedImages((prev) => {
                                const idx = prev.indexOf("LOADING");
                                if (idx !== -1) {
                                    const next = [...prev];
                                    next[idx] = finalUrl;
                                    return next;
                                }
                                return prev.length < 4 ? [...prev, finalUrl] : prev;
                            });
                        } catch (err) {
                            setAttachedImages((prev) => {
                                const idx = prev.indexOf("LOADING");
                                if (idx !== -1) {
                                    const next = [...prev];
                                    next.splice(idx, 1);
                                    return next;
                                }
                                return prev;
                            });
                            toast({ title: "Attach failed", description: "Could not upload images.", variant: "destructive" });
                        }
                    };
                    reader.onerror = () => {
                        setAttachedImages((prev) => {
                            const idx = prev.indexOf("LOADING");
                            if (idx !== -1) {
                                const next = [...prev];
                                next.splice(idx, 1);
                                return next;
                            }
                            return prev;
                        });
                        toast({ title: "Attach failed", description: "Could not read image.", variant: "destructive" });
                    };
                    reader.readAsDataURL(file);
                });
            }
        };
        window.addEventListener("dragover", handleDragOver);
        window.addEventListener("dragleave", handleDragLeave);
        window.addEventListener("drop", handleDrop);
        return () => {
            window.removeEventListener("dragover", handleDragOver);
            window.removeEventListener("dragleave", handleDragLeave);
            window.removeEventListener("drop", handleDrop);
        };
    }, [isDragging, toast]);

    const handleAddAttachedImageFromCanvas = useCallback(async (dataUrl: string) => {
        const maxAttachments = 6;
        if (attachedImages.length >= maxAttachments) {
            toast({ title: 'Attachment limit reached', variant: 'destructive' });
            return;
        }

        if (dataUrl.startsWith('http')) {
            setAttachedImages((prev) => {
                const next = [...prev, dataUrl];
                setIsEditMode(true);
                return next;
            });
            return;
        }

        // Show loader immediately then upload and replace
        setAttachedImages(prev => {
            const next = [...prev, "LOADING"].slice(0, maxAttachments);
            setIsEditMode(true);
            return next;
        });
        try {
            const finalUrl = await uploadBase64ToCloudinary(dataUrl);
            setAttachedImages(prev => {
                const idx = prev.indexOf("LOADING");
                if (idx !== -1) {
                    const next = [...prev];
                    next[idx] = finalUrl;
                    return next;
                }
                return prev.length < maxAttachments ? [...prev, finalUrl] : prev;
            });
        } catch (err) {
            setAttachedImages(prev => {
                const idx = prev.indexOf("LOADING");
                if (idx !== -1) {
                    const next = [...prev];
                    next.splice(idx, 1);
                    return next;
                }
                return prev;
            });
            toast({ title: 'Attach failed', description: 'Could not upload image.', variant: 'destructive' });
        }
    }, [attachedImages.length, toast]);

    const handleUpdateAttachedImageFromCanvas = useCallback(async (index: number, dataUrl: string) => {
        if (dataUrl.startsWith('http')) {
            setAttachedImages((prev) => {
                const next = [...prev];
                next[index] = dataUrl;
                return next;
            });
            return;
        }

        // Show loader in place then upload and replace
        setAttachedImages(prev => {
            const next = [...prev];
            next[index] = "LOADING";
            return next;
        });
        try {
            const finalUrl = await uploadBase64ToCloudinary(dataUrl);
            setAttachedImages(prev => {
                const next = [...prev];
                next[index] = finalUrl;
                return next;
            });
        } catch (err) {
            setAttachedImages(prev => {
                const next = [...prev];
                // Revert loader if needed
                next.splice(index, 1);
                return next;
            });
            toast({ title: 'Attach failed', description: 'Could not upload image.', variant: 'destructive' });
        }
    }, [toast]);

    // Download handled in layout modal

    return (
        <div className="max-w-7xl mx-auto">
            {isDragging && (
                <div className="fixed inset-0 bg-white/80 dark:bg-black/70 backdrop-blur-sm z-[9999] flex items-center justify-center p-4 pointer-events-none">
                    <div className="text-center h-[max-content] bg-transparent p-4 max-w-md mx-auto flex flex-col items-center w-full">
                        <img src={dragDropImage} alt="Drag and Drop" className="w-[300px] h-[300px] mb-6 object-contain select-none drag-none opacity-80" />
                        <h1 className="text-gray-600 text-4xl font-bold dark:text-gray-300">Drop Your Files Here</h1>
                    </div>
                </div>
            )}

            {isSearchOverlayOpen && (
                <div className="fixed inset-0 z-[200] flex items-center justify-center px-4">
                    <div
                        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
                        onClick={closeSearchOverlay}
                    />
                    <div
                        className={cn(
                            'relative w-full max-w-2xl rounded-full border shadow-2xl overflow-hidden',
                            isDarkMode
                                ? 'bg-[#1b1b1b] border-white/10'
                                : 'bg-white border-black/10'
                        )}
                    >
                        <div className="flex items-center gap-3 px-5 py-2">
                            <SearchIcon className={cn('h-5 w-5', isDarkMode ? 'text-white/70' : 'text-black/70')} />
                            <input
                                value={searchOverlayQuery}
                                onChange={(e) => setSearchOverlayQuery(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') runOverlaySearch();
                                    if (e.key === 'Escape') closeSearchOverlay();
                                }}
                                placeholder="Search images and videos"
                                className={cn(
                                    'bg-transparent outline-none w-full text-sm',
                                    isDarkMode ? 'text-white placeholder:text-white/50' : 'text-black placeholder:text-black/50'
                                )}
                                autoFocus
                            />
                            <button
                                type="button"
                                onClick={closeSearchOverlay}
                                aria-label="Close search"
                                className={cn(
                                    'p-2 rounded-full transition-colors',
                                    isDarkMode ? 'hover:bg-white/10' : 'hover:bg-black/10'
                                )}
                            >
                                <X className="h-4 w-4" />
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <div className={cn("transition-all duration-300 ease-in-out overflow-hidden", "opacity-100 translate-y-0")} style={{ maxHeight: 500 }}>
                <StyleCarousel
                    styles={styleOptions as any}
                    selectedStyle={selectedStyle}
                    onSelect={(s: any) => {
                        setSelectedStyle(s.name);
                        if (s.prompt) setPrompt(s.prompt);
                    }}
                    isDarkMode={isDarkMode}
                />
            </div>

            <div className="mb-6 flex items-center justify-between gap-3 flex-wrap">
                <h2 className="text-2xl instrument-serif-regular select-none" style={{ color: theme.text(isDarkMode) }}>
                    {searchQuery ? 'Search' : (filter === 'videos' ? 'Videos' : 'Explore')}
                </h2>
                <ChitraNavigation
                    isDarkMode={isDarkMode}
                    filter={filter}
                    onFilterChange={setFilter}
                    sortBy={sortBy}
                    onSortChange={setSortBy}
                    filterOptions={[
                        { value: "all", label: "All" },
                        { value: "images", label: "Image" },
                        { value: "videos", label: "Video" },
                    ]}
                />
            </div>

            {isGalleryLoading ? (
                <ChitraImageGridSkeleton isDarkMode={isDarkMode} isImageMode={backgroundType === 'image'} />
            ) : searchQuery && searchedItems.filter((it: any) => !it?.isGeneratingPlaceholder).length === 0 ? (
                <div className="flex flex-col items-center justify-center pt-24 pb-52 text-center">
                    <img
                        src={noSearchFound}
                        alt="No search results"
                        className="w-52 h-52 opacity-70 select-none"
                        draggable={false}
                    />
                    <div className={cn("mt-4 text-sm", isDarkMode ? "text-white/50" : "text-black/50")}>
                        No Image Found
                    </div>
                </div>
            ) : (
                <ImagesGrid
                    images={(searchQuery ? searchedItems : allItems) as any}
                    visibleCount={visibleImages}
                    isDarkMode={isDarkMode}
                    isHovered={isHovered}
                    setIsHovered={setIsHovered}
                    onOpenFullscreen={(img) => {
                        openImageFullscreen(img, (dataUrl: string) => {
                            setAttachedImages((prev) => {
                                const next = [...prev, dataUrl];
                                setIsEditMode(true);
                                return next.slice(0, 6);
                            });
                        });
                    }}
                    user={user}
                    onShowMore={() => setVisibleImages((prev) => prev + 12)}
                    isLoading={isGalleryLoading}
                    onOpenVideoFullscreen={(video) => openVideoFullscreen(video)}
                />
            )}

            {/* Bottom gradient overlay - positioned behind input area */}
            <div
                className="fixed bottom-0 left-0 right-0 h-24 pointer-events-none z-10"
                style={{
                    background: isDarkMode
                        ? 'linear-gradient(to top, rgba(18,18,18,0.95) 0%, rgba(18,18,18,0.85) 20%, rgba(18,18,18,0.4) 45%, transparent 100%)'
                        : 'linear-gradient(to top, rgba(248,248,248,0.95) 0%, rgba(248,248,248,0.85) 20%, rgba(248,248,248,0.4) 45%, transparent 100%)'
                }}
            />

            <ChitraInputArea
                prompt={prompt}
                setPrompt={setPrompt}
                isGenerating={isGenerating}
                isEditingImage={isEditingImage}
                isEnhancingPrompt={isEnhancingPrompt}
                isEditMode={isEditMode}
                attachedImages={attachedImages}
                selectedAspectRatio={selectedAspectRatio}
                showAspectRatios={showAspectRatios}
                setShowAspectRatios={setShowAspectRatios}
                isDarkMode={isDarkMode}
                onGenerate={handleGenerate}
                onEditImage={handleEditImage}
                onEnhancePrompt={enhancePrompt}
                onImageUpload={handleImageUpload}
                onClearAttachedImage={() => { setAttachedImages([]); setIsEditMode(false); setPrompt(""); }}
                onRemoveAttachedImage={(index) => {
                    setAttachedImages((prev) => {
                        const next = prev.slice();
                        next.splice(index, 1);
                        if (next.length === 0) setIsEditMode(false);
                        return next;
                    });
                }}
                onUpdateAttachedImage={handleUpdateAttachedImageFromCanvas}
                onAddAttachedImage={handleAddAttachedImageFromCanvas}
                onAspectRatioSelect={(ratio) => { setSelectedAspectRatio(ratio); setShowAspectRatios(false); }}
                onToolProcessing={(isProcessing, toolName) => {
                    setIsProcessingTool(isProcessing);
                    setProcessingToolName(toolName);
                }}
                onToolComplete={fetchGeneratedImages}
                onVideoModeClick={() => {
                    setIsVideoMode(true);
                    setShowVideoModal(true);
                }}
                onGenerateVideo={() => {
                    if (!prompt.trim()) return;
                    handleVideoGenerate({ text: prompt.trim(), avatar_id: selectedAvatar?.avatar_id, voice_id: selectedVoice?.voice_id });
                    setPrompt("");
                }}
                isVideoMode={isVideoMode}
                videoAvatarPreviewUrl={selectedAvatar?.preview_image_url}
                videoBackgroundUrl={vgBackgroundUrl || ''}
                onExitVideoMode={() => {
                    setIsVideoMode(false);
                    setSelectedAvatar(null);
                    setSelectedVoice(null);
                    setVgCaptionsEnabled(false);
                    setVgBackgroundUrl('');
                }}
            />

            <VGenSelectionModal
                isOpen={showVideoModal}
                onClose={() => setShowVideoModal(false)}
                onSelect={(avatar, voice, options) => {
                    setSelectedAvatar(avatar);
                    setSelectedVoice(voice);
                    setVgCaptionsEnabled(!!options?.captions_enabled);
                    setVgBackgroundUrl(options?.background_image_url || '');
                    setShowVideoModal(false);
                }}
                isDarkMode={isDarkMode}
            />
        </div>
    );
}
